/**
 * Two stores, deliberately not interchangeable.
 *
 * ── PREFERENCES ARE NOT ENCRYPTED. ────────────────────────────────────────
 * `@capacitor/preferences` is Android `SharedPreferences` — a plain XML file
 * any backup or rooted device reads. A token written there leaks without a
 * sound. So `preferences` is for things like "last selected tab", and anything
 * a stranger must not read goes through `secureStorage`, which on Android is
 * encrypted with a Keystore-held key.
 *
 * On the web both land in `localStorage`, which is no safer than the page's
 * own JavaScript. That is the web's ceiling, not a bug here.
 */
import { isNative } from "./platform";

/** Non-sensitive key/value strings. Native: SharedPreferences. Web: localStorage. */
export const preferences = {
  async get(key: string): Promise<string | null> {
    const { Preferences } = await import("@capacitor/preferences");
    return (await Preferences.get({ key })).value;
  },
  async remove(key: string): Promise<void> {
    const { Preferences } = await import("@capacitor/preferences");
    await Preferences.remove({ key });
  },
  async set(key: string, value: string): Promise<void> {
    const { Preferences } = await import("@capacitor/preferences");
    await Preferences.set({ key, value });
  },
};

// The plugin's own web implementation also uses localStorage, under its own
// prefix. The web branch is written out here anyway so the web build never
// loads the plugin, and so the prefix is ours and visible.
const WEB_PREFIX = "mpg.secure.";

/**
 * Secrets. Native: `@aparajita/capacitor-secure-storage` (Android Keystore).
 * Web: `localStorage`. Every method rejects on failure — the caller decides
 * what failure means; `createTokenStore` decides it means "signed out".
 */
export const secureStorage = {
  async get(key: string): Promise<string | null> {
    if (!isNative()) {
      return localStorage.getItem(WEB_PREFIX + key);
    }
    const { SecureStorage } = await import(
      "@aparajita/capacitor-secure-storage"
    );
    return SecureStorage.getItem(key);
  },
  async remove(key: string): Promise<void> {
    if (!isNative()) {
      localStorage.removeItem(WEB_PREFIX + key);
      return;
    }
    const { SecureStorage } = await import(
      "@aparajita/capacitor-secure-storage"
    );
    await SecureStorage.removeItem(key);
  },
  async set(key: string, value: string): Promise<void> {
    if (!isNative()) {
      localStorage.setItem(WEB_PREFIX + key, value);
      return;
    }
    const { SecureStorage } = await import(
      "@aparajita/capacitor-secure-storage"
    );
    await SecureStorage.setItem(key, value);
  },
};
